import { createContext, useCallback, useContext, useSyncExternalStore } from "react";

import { useStore } from "src/client/ai/hooks/useGameStore";
import type { Side } from "src/engine/rules/board";
import type { Command } from "src/engine/rules/commands";
import { type NetState, type RoomClient } from "src/engine/net/RoomClient";

export const NetContext = createContext<RoomClient | null>(null);

const OFFLINE: NetState = { status: "offline" };

/**
 * Network state for the HUD. Returns a stable offline value when there is no
 * client, so local hot-seat play needs no branching at the call site.
 */
export function useNet(): { net: NetState; client: RoomClient | null } {
  const client = useContext(NetContext);
  const net = useSyncExternalStore(
    client ? client.subscribe : () => () => {},
    client ? client.getState : () => OFFLINE,
    () => OFFLINE
  );
  return { net, client };
}

/**
 * The one place that decides whether a command goes to the local store or out
 * to the room. Every UI dispatch goes through here, so nothing has to know
 * whether this game is networked.
 */
export function useDispatch(): (command: Command) => boolean {
  const store = useStore();
  const { client } = useNet();
  return useCallback(
    (command: Command) => (client ? client.send(command) : store.dispatch(command)),
    [client, store]
  );
}

/**
 * Undo's one door (§29 U3), beside `useDispatch` and for the same reason: the
 * chrome does not know whether this game is networked.
 *
 * In a room it is a *request* — the server cuts and everybody rebuilds — so
 * `true` means "asked", not "done". Locally the cut happens here and now.
 */
export function useUndo(): (side: Side) => boolean {
  const store = useStore();
  const { client } = useNet();
  return useCallback(
    (side: Side) => (client ? client.undo() : store.undo(side) !== null),
    [client, store]
  );
}
